// The single page-header component for the customer app. Every page renders
// exactly one of these — there are no bespoke page headers anymore.
//
// Anatomy (see docs/design "Header System" archetypes):
//   • Back affordance — optional. When `backTo` is set, a single
//     "← Back to {label}" text link (13px / 600, --fg-muted). One affordance
//     only: no breadcrumbs, no chevron buttons. Omitted on top-level pages.
//   • Title — required. 26px / 700, letter-spacing -0.02em, --fg. The SAME
//     size on every page, editors included. Never shrink it.
//   • Subtitle — required, always rendered. 14px, --fg-muted, one line.
//   • Actions — optional, right-aligned on the SAME row as the title. The page
//     owns button styling (only the primary action is a filled violet button;
//     everything else is outline/ghost).
//   • Hairline — 18px bottom padding, then a 1px solid --border divider.
//
// Tabs are NOT part of the header — pages render them as a separate element
// BELOW <PageHeader/>.

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import './PageHeader.css';

export interface PageHeaderBackTo {
  label: string;
  href: string;
}

export interface PageHeaderProps {
  /** Parent link, e.g. {label: 'Workflows', href: '/workflows'}. Omit on top-level pages. */
  backTo?: PageHeaderBackTo;
  title: string;
  /** Shown right after the title on the same line (e.g. a status badge). */
  titleAddon?: ReactNode;
  subtitle: string;
  /** Right-aligned controls on the title row. Primary = filled violet; rest = outline/ghost. */
  actions?: ReactNode;
}

export function PageHeader({ backTo, title, titleAddon, subtitle, actions }: PageHeaderProps) {
  return (
    <header className="page-header">
      {backTo && (
        <div className="page-header-back">
          <Link to={backTo.href} className="page-header-back-link">
            ← Back to {backTo.label}
          </Link>
        </div>
      )}
      <div className="page-header-row">
        {titleAddon ? (
          <div className="page-header-title-group">
            <h1 className="page-header-title">{title}</h1>
            {titleAddon}
          </div>
        ) : (
          <h1 className="page-header-title">{title}</h1>
        )}
        {actions && <div className="page-header-actions">{actions}</div>}
      </div>
      <p className="page-header-subtitle">{subtitle}</p>
    </header>
  );
}

export default PageHeader;
